import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import MoodSphere from './components/MoodSphere';
import { moods, transformGoals, matchTree, type Mood, type TransformGoal } from '@/mocks/trees';

type Step = 'mood' | 'goal' | 'result';

/* Inline SVG line-art for each mood / goal */
const moodArts: Record<Mood, React.ReactNode> = {
  anxious: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="14" cy="18" r="2" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="34" cy="14" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="24" cy="10" r="1" stroke="currentColor" strokeWidth="1" />
      <path d="M12 28 Q20 24 24 30 Q28 36 36 32" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M18 36 Q24 32 30 38" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  exhausted: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="20" y="28" width="8" height="12" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M22 28 V24 Q24 20 26 24 V28" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M24 20 Q24 14 24 12" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="24" cy="10" r="2" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  empty: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="14" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="24" cy="24" r="6" stroke="currentColor" strokeWidth="1" strokeDasharray="2 2" />
      <circle cx="24" cy="24" r="1.5" fill="currentColor" />
    </svg>
  ),
  angry: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 20 L20 14 L24 20 L28 14 L34 20" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M16 30 L22 24 L26 30 L30 24 L36 30" stroke="currentColor" strokeWidth="1" fill="none" />
      <line x1="14" y1="36" x2="34" y2="36" stroke="currentColor" strokeWidth="1" />
    </svg>
  ),
  sad: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 14 Q16 22 16 26" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M24 10 Q24 18 24 24" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M32 16 Q32 22 32 28" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M20 34 Q24 38 28 34" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  lost: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 32 Q16 20 24 28 Q32 36 40 24" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M12 36 Q20 26 28 32 Q36 38 44 28" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="24" cy="14" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  lonely: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 36 L20 20 L28 28 L36 16 L40 36" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <line x1="10" y1="36" x2="42" y2="36" stroke="currentColor" strokeWidth="1" />
      <circle cx="36" cy="12" r="2" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  fearful: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 24 Q14 10 24 10 Q34 10 34 24 Q34 38 24 38 Q14 38 14 24" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M24 10 Q30 14 30 24" stroke="currentColor" strokeWidth="1" fill="none" />
      <circle cx="20" cy="22" r="1" fill="currentColor" />
      <circle cx="28" cy="22" r="1" fill="currentColor" />
    </svg>
  ),
};

const goalArts: Record<TransformGoal, React.ReactNode> = {
  calm: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 38 Q14 30 14 20 Q14 12 24 10 Q34 12 34 20 Q34 30 24 38" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M24 10 V6" stroke="currentColor" strokeWidth="1" />
      <path d="M18 32 Q24 36 30 32" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  loved: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 24 Q12 14 20 14 Q24 14 24 18 Q24 14 28 14 Q36 14 36 24 Q36 32 24 38 Q12 32 12 24" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <circle cx="20" cy="20" r="1" fill="currentColor" />
      <circle cx="28" cy="20" r="1" fill="currentColor" />
    </svg>
  ),
  fulfilled: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 38 L24 28" stroke="currentColor" strokeWidth="1.2" />
      <path d="M24 28 Q18 24 18 18 Q18 14 24 14" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M24 28 Q30 24 30 18 Q30 14 24 14" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <circle cx="24" cy="10" r="2" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M16 38 L32 38" stroke="currentColor" strokeWidth="1" />
    </svg>
  ),
  power: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 36 L20 20 L28 28 L36 14 L40 36" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <line x1="12" y1="36" x2="42" y2="36" stroke="currentColor" strokeWidth="1" />
      <circle cx="36" cy="10" r="2" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  joy: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="10" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M20 22 Q22 24 24 22 Q26 24 28 22" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M22 28 Q24 30 26 28" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M24 14 V10" stroke="currentColor" strokeWidth="1" />
      <path d="M34 24 H38" stroke="currentColor" strokeWidth="1" />
      <path d="M10 24 H14" stroke="currentColor" strokeWidth="1" />
    </svg>
  ),
  release: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 28 Q16 20 24 28 Q32 36 40 24" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M10 34 Q18 26 26 34 Q34 42 42 30" stroke="currentColor" strokeWidth="1" fill="none" />
      <path d="M12 22 Q20 14 28 22" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  safe: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="24" cy="30" rx="14" ry="8" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M10 30 Q10 18 24 18 Q38 18 38 30" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <circle cx="24" cy="14" r="2" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  free: (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 20 Q20 12 28 16 Q36 20 32 28 Q28 36 20 32 Q12 28 12 20" stroke="currentColor" strokeWidth="1.2" fill="none" />
      <path d="M32 16 L38 10" stroke="currentColor" strokeWidth="1" />
      <path d="M34 14 L36 20" stroke="currentColor" strokeWidth="1" />
      <path d="M36 12 L30 14" stroke="currentColor" strokeWidth="1" />
    </svg>
  ),
};

export default function MoodMatch() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('mood');
  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<TransformGoal | null>(null);
  const [revealed, setRevealed] = useState(false);

  const handleSelectMood = (mood: Mood) => {
    setSelectedMood(mood);
    setRevealed(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => setStep('goal'), 250);
  };

  const handleSelectGoal = (goal: TransformGoal) => {
    setSelectedGoal(goal);
    setRevealed(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setStep('result');
      setTimeout(() => setRevealed(true), 150);
    }, 250);
  };

  const handleReset = () => {
    setStep('mood');
    setSelectedMood(null);
    setSelectedGoal(null);
    setRevealed(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const match = selectedMood && selectedGoal ? matchTree(selectedMood, selectedGoal) : null;
  const moodData = selectedMood ? moods.find((m) => m.key === selectedMood) : null;
  const goalData = selectedGoal ? transformGoals.find((g) => g.key === selectedGoal) : null;

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />
      <main>
        <section className="relative py-20 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <button
              type="button"
              onClick={() => {
                if (step === 'mood') {
                  navigate('/');
                } else if (step === 'goal') {
                  setStep('mood');
                  setSelectedGoal(null);
                  setRevealed(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  setStep('mood');
                  setSelectedMood(null);
                  setSelectedGoal(null);
                  setRevealed(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm font-medium text-foreground-600 transition-colors cursor-pointer whitespace-nowrap mb-10"
            >
              <i className="ri-arrow-left-line"></i>
              返回上頁
            </button>

            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100/80 text-secondary-800 text-xs font-semibold tracking-widest uppercase mb-6 whitespace-nowrap">
                <i className="ri-heart-3-line"></i>
                Tree match 樹木配對
              </div>
              {step !== 'goal' && (
                <h2 className="font-heading text-3xl md:text-5xl font-medium text-foreground-950 leading-tight">
                  {step === 'mood' ? (
                    <span className="whitespace-nowrap">今日嘅你係咩心情？</span>
                  ) : (
                    <>
                      <span className="block whitespace-nowrap">選擇一棵樹</span>
                      <span className="block whitespace-nowrap">將情緒交比佢</span>
                    </>
                  )}
                </h2>
              )}
              {step === 'mood' && (
                <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed">
                  揀一個最貼近你當下嘅心情。慢慢嚟，唔使急。
                </p>
              )}
            </div>

            {/* Step indicator */}
            {step !== 'result' && (
              <div className="flex items-center justify-center gap-2 mb-10">
                <div className={`w-2 h-2 rounded-full transition-colors ${step === 'mood' ? 'bg-primary-500' : 'bg-primary-200'}`}></div>
                <div className="w-8 h-px bg-foreground-300/30"></div>
                <div className={`w-2 h-2 rounded-full transition-colors ${step === 'goal' ? 'bg-primary-500' : 'bg-primary-200'}`}></div>
                <div className="w-8 h-px bg-foreground-300/30"></div>
                <div className="w-2 h-2 rounded-full bg-primary-200"></div>
              </div>
            )}

            {/* Step 1: Pick Mood */}
            {step === 'mood' && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-2xl mx-auto">
                {moods.map((m) => (
                  <MoodSphere
                    key={m.key}
                    label={m.label}
                    onClick={() => handleSelectMood(m.key)}
                    art={moodArts[m.key]}
                    bgTint="bg-primary-50/60"
                    borderTint="border-primary-200/50"
                    hoverBorder="group-hover:border-primary-400/60"
                    activeBg="active:bg-primary-100/60"
                    labelColor="text-foreground-700"
                  />
                ))}
              </div>
            )}

            {/* Step 2: Pick Transform Goal */}
            {step === 'goal' && moodData && (
              <div>
                <h3 className="text-center font-heading text-2xl md:text-3xl font-medium text-foreground-950 leading-tight mb-3">
                  你想將呢種心情轉化成咩感覺？
                </h3>
                <p className="text-center text-sm text-foreground-500 mb-8">
                  揀一個你想轉化嘅方向，讓樹嘅能量陪你轉變
                </p>
                <div className="text-center mb-8">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-700 text-sm font-medium whitespace-nowrap">
                    當下心情：{moodData.label}
                    <button
                      type="button"
                      onClick={() => { setStep('mood'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="ml-1 text-primary-500 hover:text-primary-700 cursor-pointer"
                    >
                      <i className="ri-close-circle-line"></i>
                    </button>
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-xl mx-auto">
                  {transformGoals.map((g) => (
                    <MoodSphere
                      key={g.key}
                      label={g.label}
                      onClick={() => handleSelectGoal(g.key)}
                      art={goalArts[g.key]}
                      bgTint="bg-secondary-50/60"
                      borderTint="border-secondary-200/50"
                      hoverBorder="group-hover:border-secondary-400/60"
                      activeBg="active:bg-secondary-100/60"
                      labelColor="text-foreground-700"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Result - rearranged layout */}
            {step === 'result' && match && moodData && goalData && (
              <div
                className={`max-w-2xl mx-auto transition-all duration-500 ${
                  revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                }`}
              >
                {/* Mood → Goal badges */}
                <div className="flex items-center justify-center gap-3 mb-10 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium whitespace-nowrap">
                    {moodData.label}
                  </span>
                  <i className="ri-arrow-right-line text-foreground-400 text-sm"></i>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium whitespace-nowrap">
                    {goalData.label}
                  </span>
                </div>

                {/* 1. Tree Image */}
                <div className="rounded-3xl overflow-hidden mb-8">
                  <div className="relative aspect-[4/5] max-h-[500px]">
                    <img
                      src={match.image}
                      alt={`${match.treeName} 樹`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* 2. Tree Title & Species - directly below image */}
                <div className="text-center mb-8">
                  <div className="text-xs font-semibold tracking-[0.2em] uppercase text-foreground-500 mb-2">
                    Today&apos;s Tree · 今日之樹
                  </div>
                  <div className="flex items-baseline justify-center gap-2 flex-wrap">
                    <h3 className="font-heading text-3xl md:text-4xl font-medium text-foreground-950 leading-tight">
                      {match.treeName}
                    </h3>
                    <span className="font-heading text-base text-foreground-400">
                      {match.treeEn}
                    </span>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold whitespace-nowrap">
                    {match.trait}
                  </div>
                </div>

                {/* 3. Wisdom Quote Block - prominently placed right under image */}
                <div className="rounded-2xl bg-primary-50 border border-primary-100/70 p-6 md:p-8 mb-8">
                  <div className="flex gap-3">
                    <i className="ri-double-quotes-l text-2xl text-primary-400 shrink-0"></i>
                    <p className="font-heading text-lg md:text-xl leading-relaxed text-foreground-900">
                      {match.affirmation}
                    </p>
                  </div>
                </div>

                {/* 4. Detailed Tree Description & Grounding Info */}
                <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8 mb-6">
                  <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                    點解係呢棵樹？
                  </h4>
                  <p className="text-sm text-foreground-700 leading-relaxed">{match.reason}</p>

                  <div className="mt-5 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="px-5 py-2.5 rounded-full bg-background-200 hover:bg-background-300 text-sm font-medium text-foreground-800 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className="ri-arrow-go-back-line"></i>
                      重新配對
                    </button>
                  </div>
                </div>

                {/* Grounding tip */}
                <div className="p-5 rounded-2xl bg-background-50 border border-background-200/60">
                  <div className="flex gap-3 items-start">
                    <i className="ri-lightbulb-flash-line text-lg text-accent-500 mt-0.5 shrink-0"></i>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      提示：唔一定要跟足呢棵樹去抱，任何樹木都可以畀到你力量，順從你當下嘅直覺即可。
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}